
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { PromoCode } from "@/types/supabase";

interface QuoteEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingDetails: {
    checkIn: Date;
    checkOut: Date;
    nights: number;
    subtotal: number;
    cleaningFee: number;
    touristTax: number;
    total: number;
    discount: number;
    guestsCount?: number;
  } | null;
  guestsCount: number;
  appliedPromoCode: PromoCode | null;
}

const QuoteEmailDialog = ({
  open,
  onOpenChange,
  bookingDetails,
  guestsCount,
  appliedPromoCode,
}: QuoteEmailDialogProps) => {
  const [email, setEmail] = useState("");
  const [isSendingQuote, setIsSendingQuote] = useState(false);

  const handleSendQuote = async () => {
    if (!email || !bookingDetails) {
      toast.error("Veuillez saisir votre email pour recevoir le devis");
      return;
    }

    setIsSendingQuote(true);
    try {
      console.log("Envoi du devis à", email);
      console.log("Détails du devis:", {
        checkIn: bookingDetails.checkIn.toISOString(),
        checkOut: bookingDetails.checkOut.toISOString(),
        nights: bookingDetails.nights,
        subtotal: bookingDetails.subtotal,
        cleaningFee: bookingDetails.cleaningFee,
        touristTax: bookingDetails.touristTax,
        discount: bookingDetails.discount,
        total: bookingDetails.total,
        guestsCount: bookingDetails.guestsCount || guestsCount
      });

      const { data, error } = await supabase.functions.invoke('send-quote', {
        body: {
          email,
          quote: {
            checkIn: bookingDetails.checkIn.toISOString(),
            checkOut: bookingDetails.checkOut.toISOString(),
            nights: bookingDetails.nights,
            subtotal: bookingDetails.subtotal,
            cleaningFee: bookingDetails.cleaningFee,
            touristTax: bookingDetails.touristTax,
            discount: bookingDetails.discount,
            total: bookingDetails.total,
            guestsCount: bookingDetails.guestsCount || guestsCount
          }
        }
      });

      if (error) {
        console.error("Erreur lors de l'appel de la fonction Edge:", error);
        throw new Error(`Erreur lors de l'envoi du devis: ${error.message}`);
      }

      console.log("Réponse de la fonction edge:", data);

      try {
        const { data: quoteData, error: dbError } = await (supabase.from('quotes') as any).insert([{
          email,
          check_in: format(bookingDetails.checkIn, 'yyyy-MM-dd'),
          check_out: format(bookingDetails.checkOut, 'yyyy-MM-dd'),
          nights: bookingDetails.nights,
          subtotal: bookingDetails.subtotal,
          cleaning_fee: bookingDetails.cleaningFee,
          tourist_tax: bookingDetails.touristTax,
          discount: bookingDetails.discount,
          total: bookingDetails.total,
          guests_count: bookingDetails.guestsCount || guestsCount,
          promo_code: appliedPromoCode?.code || null
        }]).select().single();

        if (dbError) {
          console.error("Erreur lors de l'enregistrement du devis:", dbError);
        } else {
          // Send push notification to admins
          try {
            await supabase.functions.invoke('send-push-notification', {
              body: {
                title: '📋 Nouvelle demande de devis',
                body: `${email} a demandé un devis (${bookingDetails.nights} nuits)`,
                url: '/admin',
                tag: 'quote_request',
                notification_type: 'quote_request'
              }
            });
          } catch (notifError) {
            console.error("Error sending push notification:", notifError);
          }
        }
      } catch (dbError) {
        console.error("Erreur lors de l'enregistrement en base de données:", dbError);
        // On continue car l'email a été envoyé, c'est le plus important
      }

      toast.success("Devis envoyé avec succès à votre adresse email !");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erreur complète:", error);
      toast.error(error.message || "Une erreur est survenue lors de l'envoi du devis");
    } finally {
      setIsSendingQuote(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Recevoir votre devis par email</DialogTitle>
          <DialogDescription>
            Nous vous enverrons un récapitulatif détaillé de votre séjour.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="quote-email">Votre adresse email</Label>
            <Input
              id="quote-email"
              type="email"
              placeholder="email@exemple.fr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            className="bg-villa-turquoise hover:bg-villa-turquoise/90"
            disabled={isSendingQuote}
            onClick={handleSendQuote}
          >
            {isSendingQuote ? "Envoi en cours..." : "Envoyer le devis"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteEmailDialog;
